import { useCallback, useState } from "react";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { FuncionarioTabela } from "../../domain/models/funcionario_tabela";
import { useGestaoTreinamentosListaFilters } from "../hooks/GestaoTreinamentos_lista/filters";
import { useGestaoTreinamentosListaPagination } from "../hooks/GestaoTreinamentos_lista/pagination";
import { processError } from "../../../../common/utils/base_error";
import { useNavigate } from "react-router";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { TelasIds } from "../../../../common/utils/tela_ids";

/**
 * LoadFuncionarioState
 */

type LoadFuncionarioState = {
  tipoPesquisa?: number,
  pesquisa?: string,
  setor?: string,
  filial?: string,
  admissaoDe?: string,
  admissaoAte?: string,
  demissaoDe?: string,
  demissaoAte?: string,
  afastamentoDe?: string,
  afastamentoAte?: string,
  feriasDe?: string,
  feriasAte?: string,
  treinamento?: number,
  prazoExperiencia?: number,
  documento?: number,
  centroDeCusto?: number[],
  status?: number,
  page?: number,
  pageSize?: number,
};

/**
 * useGestaoTreinamentosListaViewModel
 */

export const useGestaoTreinamentosListaViewModel = () => {
  const {
    commons: {
      getCentroCustoDropDownUseCase,
    },
    rh: {
      getFuncionariosUseCase,
    },
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);

  const { pushErrorNotification } = usePageLoader();

  const [errorMessage, setErrorMessage] = useState<string>();
  const [funcionarios, setFuncionarios] = useState<FuncionarioTabela[]>();
  const [loading, setLoading] = useState(false);

  const [showLegenda, setShowLegenda] = useState(false);

  const navigate = useNavigate();

  const filters = useGestaoTreinamentosListaFilters();
  const pagination = useGestaoTreinamentosListaPagination();

  /**
   * Funcoes de carregamento
   */

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      const centroCusto = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      filters.setCentroDeCustoLista([...centroCusto]);
    } catch (e: unknown) {
      throw e;
    }
  }, [
    filters,
    getCentroCustoDropDownUseCase,
  ]);

  const loadFuncionarios = useCallback(async (funcState?: LoadFuncionarioState) => {
    try {
      const filteredTipoPesquisa = funcState?.tipoPesquisa ?? filters.tipoPesquisa;
      const filteredPesquisa = funcState?.pesquisa ?? filters.pesquisa;
      const filteredSetor = funcState?.setor ?? filters.setor;
      const filteredFilial = funcState?.filial ?? filters.filial;
      const filteredAdmissaoDe = funcState?.admissaoDe ?? filters.admissaoDe;
      const filteredAdmissaoAte = funcState?.admissaoAte ?? filters.admissaoAte;
      const filteredDemissaoDe = funcState?.demissaoDe ?? filters.demissaoDe;
      const filteredDemissaoAte = funcState?.demissaoAte ?? filters.demissaoAte;
      const filteredAfastamentoDe = funcState?.afastamentoDe ?? filters.afastamentoDe;
      const filteredAfastamentoAte = funcState?.afastamentoAte ?? filters.afastamentoAte;
      const filteredFeriasDe = funcState?.feriasDe ?? filters.feriasDe;
      const filteredFeriasAte = funcState?.feriasAte ?? filters.feriasAte;
      const filteredTreinamento = funcState?.treinamento ?? filters.treinamentos;
      const filteredPrazoExperiencia = funcState?.prazoExperiencia ?? filters.prazoExperiencia;
      const filteredDocumento = funcState?.documento ?? filters.documentos;
      const filteredCentroDeCusto = funcState?.centroDeCusto ?? filters.centroDeCusto;
      const filteredStatus = funcState?.status ?? filters.status;

      const page = funcState?.page ?? pagination.funcionarios.page;
      const rowsPerPage = funcState?.pageSize ?? pagination.funcionarios.rowsPerPage;

      await getFuncionariosUseCase.getFuncionarios(
        TelasIds.GestaoTreinamentos_lista, {
        tipoPesquisa: filteredTipoPesquisa,
        pesquisa: filteredPesquisa,
        setor: filteredSetor,
        filial: filteredFilial,
        admissaoDe: filteredAdmissaoDe,
        admissaoAte: filteredAdmissaoAte,
        demissaoDe: filteredDemissaoDe,
        demissaoAte: filteredDemissaoAte,
        afastamentoDe: filteredAfastamentoDe,
        afastamentoAte: filteredAfastamentoAte,
        feriasDe: filteredFeriasDe,
        feriasAte: filteredFeriasAte,
        treinamentos: filteredTreinamento,
        prazoExperiencia: filteredPrazoExperiencia,
        documentos: filteredDocumento,
        centroDeCusto: filteredCentroDeCusto,
        status: filteredStatus,
      },
        '',
        'asc',
        page,
        rowsPerPage,
      );

      setFuncionarios(getFuncionariosUseCase.items);

      pagination.funcionariosSetter.setRowsPerPage(getFuncionariosUseCase.pageSize);
      pagination.funcionariosSetter.setTotalItems(getFuncionariosUseCase.totalItems);
    } catch (e: unknown) {
      setFuncionarios([]);
      throw e;
    }
  }, [
    filters,
    pagination,
    getFuncionariosUseCase,
  ]);

  /**
   * useEffects
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined)
      await loadCentroCustoDropDown();
      await loadFuncionarios();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao carregar pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReloadPage = useCallback(async () => {
    try {
      setLoading(true);
      await loadFuncionarios();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleTipoPesquisaChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useGestaoTreinamentosListaFilters.getState().setTipoPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ tipoPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handlePesquisaChange = useDebouncedCallback(debouncedRef, (newValue: string) => {
    useGestaoTreinamentosListaFilters.getState().setPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ pesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleFilialChange = useDebouncedCallback(debouncedRef, (newValue: string) => {
    useGestaoTreinamentosListaFilters.getState().setFilial(newValue);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ filial: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleSetorChange = useDebouncedCallback(debouncedRef, (newValue: string) => {
    useGestaoTreinamentosListaFilters.getState().setSetor(newValue);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ setor: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleAdmissaoChange = useDebouncedCallback(debouncedRef, (de: string, ate: string) => {
    useGestaoTreinamentosListaFilters.getState().setAdmissaoDe(de);
    useGestaoTreinamentosListaFilters.getState().setAdmissaoAte(ate);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ admissaoDe: de, admissaoAte: ate, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleDemissaoChange = useDebouncedCallback(debouncedRef, (de: string, ate: string) => {
    useGestaoTreinamentosListaFilters.getState().setDemissaoDe(de);
    useGestaoTreinamentosListaFilters.getState().setDemissaoAte(ate);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ demissaoDe: de, demissaoAte: ate, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleAfastamentoChange = useDebouncedCallback(debouncedRef, (de: string, ate: string) => {
    useGestaoTreinamentosListaFilters.getState().setAfastamentoDe(de);
    useGestaoTreinamentosListaFilters.getState().setAfastamentoAte(ate);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ afastamentoDe: de, afastamentoAte: ate, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleFeriasChange = useDebouncedCallback(debouncedRef, (de: string, ate: string) => {
    useGestaoTreinamentosListaFilters.getState().setFeriasDe(de);
    useGestaoTreinamentosListaFilters.getState().setFeriasAte(ate);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ feriasDe: de, feriasAte: ate, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleTreinamento = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useGestaoTreinamentosListaFilters.getState().setTreinamento(newValue);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ treinamento: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handlePrazoExperiencia = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useGestaoTreinamentosListaFilters.getState().setPrazoExperiencia(newValue);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ prazoExperiencia: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleDocumento = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useGestaoTreinamentosListaFilters.getState().setDocumento(newValue);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ documento: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleCentroDeCustoChange = useDebouncedCallback(debouncedRef, (newValue: number[]) => {
    useGestaoTreinamentosListaFilters.getState().setCentroDeCusto(newValue);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ centroDeCusto: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleStatusChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useGestaoTreinamentosListaFilters.getState().setStatus(newValue);
    return async () => {
      try {
        setLoading(true);
        useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
        await loadFuncionarios({ status: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
      } finally {
        setLoading(false);
      }
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);

      useGestaoTreinamentosListaFilters.getState().clear();

      await loadFuncionarios({
        tipoPesquisa: 0,
        pesquisa: "",
        setor: "",
        filial: "",
        admissaoDe: "",
        admissaoAte: "",
        demissaoDe: "",
        demissaoAte: "",
        afastamentoDe: "",
        afastamentoAte: "",
        feriasDe: "",
        feriasAte: "",
        centroDeCusto: [],
        status: 1,
        page: 1,
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar relatorio!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(page + 1);
      await loadFuncionarios({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setPage(1);
      useGestaoTreinamentosListaPagination.getState().funcionariosSetter.setRowsPerPage(rowsPerPage);
      await loadFuncionarios({ page: 1, pageSize: rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadFuncionarios,
    pushErrorNotification,
  ]);

  const handleGestaoTreinamentosCadastroOpenState = useCallback(async (idFuncionario: number | null) => {
    if (idFuncionario) {
      navigate(`/GestaoTreinamentos_cadastro?id=${idFuncionario}`);
    }
  }, [
    navigate,
  ]);

  const handleShowLegenda = useCallback((openState: boolean) => {
    setShowLegenda(openState);
  }, [
  ]);

  return {
    loading,
    errorMessage,
    funcionarios: funcionarios ?? [],
    showLegenda,
    filters,
    pagination,
    handleReloadPage,
    handleTipoPesquisaChange,
    handlePesquisaChange,
    handleSetorChange,
    handleFilialChange,
    handleAdmissaoChange,
    handleDemissaoChange,
    handleAfastamentoChange,
    handleFeriasChange,
    handleTreinamento,
    handlePrazoExperiencia,
    handleDocumento,
    handleCentroDeCustoChange,
    handleStatusChange,
    handleClearFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleGestaoTreinamentosCadastroOpenState,
    handleShowLegenda,
  };
}